import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, CheckCircle2, CircleDot, Clock, Plus, Trash2, User, X, AlertTriangle, Ban,
} from "lucide-react";

const PHASE_STATUSES = ["pending", "in_progress", "completed", "blocked"];
const PROJECT_STATUSES = ["development", "on_hold", "delivered", "cancelled"];

const phaseIcon = (status) => {
  if (status === "completed") return <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />;
  if (status === "in_progress") return <CircleDot className="w-4 h-4 animate-pulse" strokeWidth={2.5} />;
  if (status === "blocked") return <Ban className="w-4 h-4" strokeWidth={2.5} />;
  return <Clock className="w-4 h-4" strokeWidth={2.5} />;
};

const phaseTint = (status) => ({
  completed:   "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]",
  in_progress: "bg-[var(--peak-accent)] text-white border-[var(--peak-accent)]",
  blocked:     "bg-amber-100 text-amber-900 border-amber-800",
  pending:     "bg-white text-[var(--peak-ink)] border-[var(--peak-ink)]",
}[status] || "bg-white text-[var(--peak-ink)] border-[var(--peak-ink)]");

const money = (n) => (n ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");

export default function DevProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesDraft, setNotesDraft] = useState("");
  const [etaDraft, setEtaDraft] = useState("");
  const [showAddPhase, setShowAddPhase] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get(`/development/projects/${id}`),
        api.get("/development/admins"),
      ]);
      setProject(p.data);
      setAdmins(a.data || []);
      setNotesDraft(p.data.notes || "");
      setEtaDraft((p.data.eta || "").slice(0, 10));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patchProject = async (body) => {
    try {
      const { data } = await api.patch(`/development/projects/${id}`, body);
      setProject(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      throw e;
    }
  };

  const setStatus = (status) => patchProject({ status }).then(() => toast.success("Status updated"));
  const setAdmin = (adminId) => {
    const admin = admins.find((a) => a.id === adminId);
    if (!admin) return;
    patchProject({
      assigned_admin_id: admin.id,
      assigned_admin_name: admin.name || admin.email,
    }).then(() => toast.success("Assigned"));
  };
  const saveNotes = () => patchProject({ notes: notesDraft }).then(() => toast.success("Notes saved"));
  const saveEta = () => {
    const iso = etaDraft ? new Date(etaDraft).toISOString() : null;
    patchProject({ eta: iso }).then(() => toast.success("ETA saved"));
  };

  const updatePhase = async (phaseId, body) => {
    try {
      await api.patch(`/development/projects/${id}/phases/${phaseId}`, body);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  const deletePhase = async (phaseId) => {
    if (!window.confirm("Delete this phase?")) return;
    try {
      await api.delete(`/development/projects/${id}/phases/${phaseId}`);
      await load();
      toast.success("Phase removed");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  const addPhase = async (payload) => {
    try {
      await api.post(`/development/projects/${id}/phases`, payload);
      await load();
      toast.success("Phase added");
      setShowAddPhase(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  if (!user?.is_superadmin) {
    return <div className="p-10 text-sm">Superadmin only.</div>;
  }

  if (loading || !project) {
    return <div className="p-10 text-sm" data-testid="dev-detail-loading">Loading…</div>;
  }

  return (
    <div className="min-h-screen" data-testid="dev-detail-page">
      {/* Header */}
      <div className="border-b-2 border-[var(--peak-ink)] bg-white">
        <div className="px-8 py-6">
          <button
            onClick={() => nav("/superadmin/development")}
            data-testid="dev-back-btn"
            className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] hover:text-[var(--peak-accent)] flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Development
          </button>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-accent)]">{project.product_type || "Custom Build"}</div>
              <h1 className="font-heading text-4xl font-black tracking-tighter mt-1 break-words">{project.project_name}</h1>
              <div className="text-sm text-[var(--peak-muted)] mt-1">
                {project.client_name}
                {project.value ? <> · <span className="font-mono">{money(project.value)}</span></> : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={project.status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="dev-status-select"
                className="h-10 border-2 border-[var(--peak-ink)] bg-white px-3 text-xs font-mono uppercase tracking-wider"
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
              {project.peak_deal_id && (
                <Link
                  to="/pipelines"
                  data-testid="dev-view-deal-btn"
                  className="h-10 flex items-center px-3 text-xs font-heading font-bold uppercase border-2 border-[var(--peak-ink)] bg-white hover:bg-[var(--peak-ink)] hover:text-white transition"
                >
                  View Deal →
                </Link>
              )}
              {project.propgen_project_id && (
                <Link
                  to={`/superadmin/propgen/projects/${project.propgen_project_id}`}
                  data-testid="dev-view-propgen-btn"
                  className="h-10 flex items-center px-3 text-xs font-heading font-bold uppercase border-2 border-[var(--peak-ink)] bg-white hover:bg-[var(--peak-ink)] hover:text-white transition"
                >
                  Open PropGen →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
        {/* Timeline column */}
        <div className="space-y-6">
          <div className="border-2 border-[var(--peak-ink)] bg-white">
            <div className="border-b-2 border-[var(--peak-ink)] px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">Build Timeline</div>
                <div className="font-heading text-xl font-black tracking-tight">Phases</div>
              </div>
              <Button
                onClick={() => setShowAddPhase(true)}
                data-testid="dev-add-phase-btn"
                className="h-9 bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-heading font-bold uppercase text-xs tracking-wide"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Phase
              </Button>
            </div>

            <ol className="p-6 space-y-3" data-testid="dev-phase-list">
              {(project.phases || []).map((p, idx) => (
                <li key={p.id} className="flex items-start gap-3" data-testid={`dev-phase-${p.id}`}>
                  <div className={`flex-none w-8 h-8 border-2 flex items-center justify-center ${phaseTint(p.status)}`}>
                    {phaseIcon(p.status)}
                  </div>
                  <div className="flex-1 min-w-0 border-2 border-[var(--peak-ink)] p-3 bg-[var(--peak-bg)]">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">Phase {idx + 1}</div>
                        <div className="font-heading font-bold text-base">{p.name}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={p.status}
                          onChange={(e) => updatePhase(p.id, { status: e.target.value })}
                          data-testid={`dev-phase-status-${p.id}`}
                          className="h-8 border-2 border-[var(--peak-ink)] bg-white px-2 text-[11px] font-mono uppercase"
                        >
                          {PHASE_STATUSES.map((s) => (
                            <option key={s} value={s}>{s.replace("_", " ")}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => deletePhase(p.id)}
                          data-testid={`dev-phase-delete-${p.id}`}
                          className="h-8 w-8 flex items-center justify-center border-2 border-[var(--peak-ink)] hover:bg-red-100"
                          title="Delete phase"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {p.description && (
                      <div className="text-[13px] text-[var(--peak-muted)] mt-2 leading-relaxed">{p.description}</div>
                    )}
                    <div className="text-[10px] font-mono text-[var(--peak-muted)] mt-2 flex flex-wrap gap-3">
                      {p.started_at && <span>Started {new Date(p.started_at).toLocaleDateString()}</span>}
                      {p.completed_at && <span>Done {new Date(p.completed_at).toLocaleDateString()}</span>}
                      {p.eta && <span>ETA {new Date(p.eta).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </li>
              ))}
              {(!project.phases || project.phases.length === 0) && (
                <div className="text-sm text-[var(--peak-muted)] text-center py-6">No phases yet. Add one to get started.</div>
              )}
            </ol>
          </div>

          {/* Requirements panel */}
          <div className="border-2 border-[var(--peak-ink)] bg-white">
            <div className="border-b-2 border-[var(--peak-ink)] px-6 py-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">Scope Snapshot</div>
              <div className="font-heading text-xl font-black tracking-tight">Client Needs & Build Notes</div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <ScopeList
                label="Client Needs (Open Items)"
                items={project.client_needs}
                icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
                testid="scope-client-needs"
                emptyLabel="None outstanding."
              />
              <ScopeList
                label="Integrations"
                items={project.integrations}
                testid="scope-integrations"
                emptyLabel="No third-party integrations."
              />
              <ScopeList
                label="Assumptions"
                items={project.assumptions}
                testid="scope-assumptions"
                emptyLabel="No assumptions logged."
              />
              <ScopeList
                label="Risks"
                items={project.risks}
                icon={<AlertTriangle className="w-4 h-4 text-red-600" />}
                testid="scope-risks"
                emptyLabel="No risks logged."
              />
            </div>
            {project.features && project.features.length > 0 && (
              <div className="px-6 pb-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] mb-2">Features ({project.features.length})</div>
                <div className="flex flex-wrap gap-1.5">
                  {project.features.map((f, i) => (
                    <span
                      key={i}
                      className="text-[11px] font-mono border border-[var(--peak-ink)] bg-white px-2 py-1"
                      data-testid={`feature-chip-${i}`}
                    >
                      {f.name}
                      {f.category ? <span className="text-[var(--peak-muted)]"> · {f.category}</span> : null}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="border-2 border-[var(--peak-ink)] bg-white p-5 space-y-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">Project Meta</div>

            <MetaRow label="Progress">
              <div className="w-full h-2 bg-[var(--peak-border)] border border-[var(--peak-ink)]">
                <div
                  className="h-full bg-[var(--peak-accent)]"
                  style={{ width: `${Math.min(100, project.progress || 0)}%` }}
                />
              </div>
              <div className="text-[10px] font-mono text-[var(--peak-muted)] mt-1">{project.progress || 0}%</div>
            </MetaRow>

            <MetaRow label="Assigned admin">
              <select
                value={project.assigned_admin_id || ""}
                onChange={(e) => setAdmin(e.target.value)}
                data-testid="dev-admin-select"
                className="w-full h-10 border-2 border-[var(--peak-ink)] bg-white px-3 text-xs font-mono"
              >
                <option value="">Unassigned</option>
                {admins.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.email}</option>
                ))}
              </select>
            </MetaRow>

            <MetaRow label="Target ETA">
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={etaDraft}
                  onChange={(e) => setEtaDraft(e.target.value)}
                  data-testid="dev-eta-input"
                  className="h-10 border-2 border-[var(--peak-ink)] font-mono text-xs"
                />
                <Button
                  onClick={saveEta}
                  data-testid="dev-eta-save-btn"
                  className="h-10 bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-heading font-bold uppercase text-[10px] tracking-wide"
                >
                  Save
                </Button>
              </div>
            </MetaRow>

            <MetaRow label="Started">
              <div className="text-sm font-mono">{project.started_at ? new Date(project.started_at).toLocaleDateString() : "—"}</div>
            </MetaRow>

            <MetaRow label="Value">
              <div className="text-sm font-mono">{money(project.value)}</div>
            </MetaRow>

            <MetaRow label="Urgency / Design">
              <div className="text-sm font-mono uppercase">{project.urgency || "—"} / {project.design_complexity || "—"}</div>
            </MetaRow>
          </div>

          <div className="border-2 border-[var(--peak-ink)] bg-white p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] mb-2">Internal Notes</div>
            <Textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={6}
              placeholder="Delivery notes, blockers, client quirks…"
              data-testid="dev-notes-textarea"
              className="border-2 border-[var(--peak-ink)] font-mono text-xs"
            />
            <Button
              onClick={saveNotes}
              data-testid="dev-notes-save-btn"
              className="mt-3 w-full h-10 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              Save notes
            </Button>
          </div>

          {project.target_users && project.target_users.length > 0 && (
            <div className="border-2 border-[var(--peak-ink)] bg-white p-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] mb-2">Target Users</div>
              <ul className="space-y-1">
                {project.target_users.map((u, i) => (
                  <li key={i} className="text-sm flex items-center gap-2" data-testid={`target-user-${i}`}>
                    <User className="w-3.5 h-3.5 text-[var(--peak-muted)]" />
                    {u}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {showAddPhase && (
        <AddPhaseDialog onClose={() => setShowAddPhase(false)} onCreate={addPhase} />
      )}
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function ScopeList({ label, items, icon, testid, emptyLabel }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] mb-2 flex items-center gap-1.5">
        {icon}{label}
      </div>
      {items && items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={i} className="text-[13px] leading-relaxed flex gap-2">
              <span className="text-[var(--peak-accent)] font-bold flex-none">›</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-[var(--peak-muted)] italic">{emptyLabel}</div>
      )}
    </div>
  );
}

function AddPhaseDialog({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("pending");
  const [eta, setEta] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim(),
        status,
        eta: eta ? new Date(eta).toISOString() : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" data-testid="dev-add-phase-dialog">
      <div className="bg-white border-2 border-[var(--peak-ink)] w-full max-w-md">
        <div className="border-b-2 border-[var(--peak-ink)] px-5 py-4 flex items-center justify-between">
          <div className="font-heading text-lg font-black tracking-tight">Add Phase</div>
          <button onClick={onClose} className="p-1" data-testid="dev-add-phase-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] block mb-1.5">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Integrations: Stripe + Twilio"
              data-testid="dev-add-phase-name"
              className="h-10 border-2 border-[var(--peak-ink)]"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] block mb-1.5">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="dev-add-phase-desc"
              className="border-2 border-[var(--peak-ink)]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] block mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="dev-add-phase-status"
                className="w-full h-10 border-2 border-[var(--peak-ink)] bg-white px-3 text-xs font-mono uppercase"
              >
                {PHASE_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace("_", " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] block mb-1.5">ETA</label>
              <Input
                type="date"
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                data-testid="dev-add-phase-eta"
                className="h-10 border-2 border-[var(--peak-ink)] font-mono text-xs"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 border-2 border-[var(--peak-ink)] text-xs font-heading font-bold uppercase"
              data-testid="dev-add-phase-cancel"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={!name.trim() || submitting}
              data-testid="dev-add-phase-submit"
              className="h-10 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              {submitting ? "Adding…" : "Add Phase"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
